import api from 'api';
import {
  TIC_MEMORY_VIEWMODE,
  type TicMemoryViewmode,
  TicMemoryViewmodeSchema,
} from 'constants/user';
import { showToast } from 'redux/modules/toasts.actions';
import type {
  AllAcceptedUserPropertyKey,
  UserPermissions,
  UserPropertyValue,
  UserState,
} from 'redux/modules/user.types';
import {
  emulatePermissions as emulatePermissionsPure,
  emulateRole as emulateRolePure,
  fetchedUser,
  savedUser,
  setUserAccountSettings,
  setUserGlobalPermissions,
  setUserPermissions,
  setUserProperties,
  setUserProperty,
  stopEmulatingPermissions as stopEmulatingPermissionsPure,
  stopEmulatingRole as stopEmulatingRolePure,
  userXhrStatusModule,
} from 'redux/modules/user.types';
import { getHasTicForcedEnabled } from 'redux/selectors/accounts';
import { getUserId, getUserProperties } from 'redux/selectors/user';
import { TrackingActions } from 'tracking/actions';
import TimelyAnalytics from 'tracking/timely-analytics';
import type { Thunk } from 'types/redux';
import type { User } from 'types/user';
import type { Merge } from 'types/utility';
import { cloneDeep } from 'utils';
import { deserializeProperties, flattenUserPermissions, serializeProperty } from 'utils/user';

export {
  clearTour,
  fetchedUser,
  restartTour,
  savedUser,
  setUserAccountSettings,
  setUserPermissions,
  setUserProperties,
  setUserProperty,
} from 'redux/modules/user.types';

const { wrapFetching, wrapSaving } = userXhrStatusModule;

export function fetchUserProperties(): Thunk<Promise<unknown>> {
  return async dispatch => {
    try {
      const result = await dispatch(wrapFetching(api.getUserProperties(), 'user-properties'));
      const properties = deserializeProperties(result);
      dispatch(setUserProperties(properties));
    } catch (err) {
      dispatch(
        showToast({
          message: 'Error fetching user properties',
          type: 'danger',
        })
      );
    }
  };
}

export const saveUserProperty =
  (
    property: AllAcceptedUserPropertyKey,
    value: UserPropertyValue = true,
    optimisticUpdate: boolean = true
  ): Thunk<Promise<unknown>> =>
  async (dispatch, getState) => {
    const state = getState();

    // Block user from disabling TIC if it's forced enabled for account.
    // Unlikely to happen, but as safe-guard just in case.
    if (property === 'has_enabled_tic' && !value && getHasTicForcedEnabled(state)) {
      dispatch(showToast({ message: 'Account settings prevent opting out', type: 'warning' }));
      return Promise.resolve();
    }

    const userProperties = getUserProperties(state);
    const userId = getUserId(state);

    if (userProperties[property] === value) {
      return Promise.resolve();
    }

    const payload = serializeProperty({
      property,
      value,
      user_id: userId,
    });

    const oldValue = userProperties[property];

    if (optimisticUpdate) {
      // Optimistic update before xhr:
      dispatch(setUserProperty({ [property]: value }));
    }

    try {
      await dispatch(wrapSaving(api.saveUserProperty(payload)));
      if (!optimisticUpdate) {
        dispatch(setUserProperty({ [property]: value }));
      }
      TrackingActions.saveUserProperty(property, value);
    } catch (err) {
      // rollback optimistic update
      dispatch(setUserProperty({ [property]: oldValue }));
    }
  };

export function fetchUser(): Thunk {
  return dispatch => {
    return dispatch(wrapFetching(api.getUser())).then(user => {
      // The user object in the bootstrapped data is read by our analytics code outside of the
      // React tree, so we need to keep it in sync
      Object.assign(window.BOOTSTRAPPED_DATA.user, cloneDeep(user));
      dispatch(fetchedUser(user));
    });
  };
}

export function acceptMemoryRetentionLimit(days: number): Thunk<Promise<unknown>> {
  return dispatch => {
    return dispatch(saveUser({ memory_retention_days: days }));
  };
}

export function saveUser(data: Partial<User>): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const payload: Merge<Partial<User>, { id: number }> = {
      ...data,
      // When user id to save is unspecified,
      // default to logged in user
      id: data.id ?? getState().user.id,
    };

    return dispatch(wrapSaving(api.saveUser(payload))).then(user => {
      // The user object in the bootstrapped data is read by our analytics code outside of the
      // React tree, so we need to keep it in sync
      Object.assign(window.BOOTSTRAPPED_DATA.user, cloneDeep(user));
      dispatch(savedUser(user));
    });
  };
}

export function getUserAccountSettings(): Thunk<Promise<UserState['accountSettings']>> {
  return dispatch =>
    api.getUserAccountSettings().then(payload => {
      dispatch(setUserAccountSettings(payload));
      return payload;
    });
}

export function updateUserAccountSettings(
  settings: Record<string, string | number | boolean> = {}
): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    // We dispatch `SET_USER_ACCOUNT_SETTINGS` twice here in order for the
    // UI to update optimistically instead of having to wait for the network
    // request to come back

    // Once here with with the old accountSettings
    dispatch(
      setUserAccountSettings({
        ...getState().user.accountSettings,
        ...settings,
      })
    );

    return api.updateUserAccountSettings(settings).then(payload => {
      // Update with potentially other new accountSettings from the database
      dispatch(setUserAccountSettings(payload));
    });
  };
}

export const fetchUserPermissions = (): Thunk<Promise<void>> => async dispatch => {
  const response = await api.getUserPermissions();
  const { permissions, globalPermissions } = flattenUserPermissions(response);
  dispatch(setUserPermissions(permissions));
  dispatch(setUserGlobalPermissions(globalPermissions));
};

const emulatedPermissionsStorageKey = 'emulatedPermissions';
const emulatedRoleStorageKey = 'emulatedRole';

export const emulatePermissions =
  (permissions: UserPermissions, roleId: number): Thunk =>
  dispatch => {
    try {
      sessionStorage.setItem(emulatedPermissionsStorageKey, JSON.stringify(permissions));
      sessionStorage.setItem(emulatedRoleStorageKey, roleId.toString());
    } finally {
      dispatch(emulatePermissionsPure(permissions));
      dispatch(emulateRolePure(roleId));
    }
  };

export const stopEmulatingPermissions = (): Thunk => dispatch => {
  try {
    sessionStorage.removeItem(emulatedPermissionsStorageKey);
    sessionStorage.removeItem(emulatedRoleStorageKey);
  } finally {
    dispatch(stopEmulatingPermissionsPure());
    dispatch(stopEmulatingRolePure());
  }
};

export const fetchEmulatedPermissionsFromCache = (): Thunk => dispatch => {
  try {
    const serializedPermissions = sessionStorage.getItem(emulatedPermissionsStorageKey);
    if (serializedPermissions) {
      const permissions: UserPermissions = JSON.parse(serializedPermissions);
      dispatch(emulatePermissionsPure(permissions));
    }
  } catch {}

  try {
    const serializedRoleId = sessionStorage.getItem(emulatedRoleStorageKey);
    if (serializedRoleId && !isNaN(+serializedRoleId)) {
      dispatch(emulateRolePure(+serializedRoleId));
    }
  } catch {}
};

export function setTicMemoryViewmode(viewMode: TicMemoryViewmode): Thunk {
  return dispatch => {
    const parsed = TicMemoryViewmodeSchema.safeParse(viewMode).data;
    if (!parsed) {
      throw new Error(`Invalid TicMemoryViewmode: ${viewMode}`);
    }
    dispatch(saveUserProperty(TIC_MEMORY_VIEWMODE, viewMode, true));
    TimelyAnalytics.track('Set Memory View', {
      memory_view: viewMode,
    });
  };
}
